import {
  Card,
  Correctness,
  DailyNewWords,
  LearnableId,
  LearnableWithProgress,
  LearningSessionError,
  NextSession,
  PresentationCard,
  ProgressPreferences,
  SummaryItem,
  TestCard,
  UserPath,
  UserScenario,
} from 'src/api/learningSessions/types';
import { CardData, PresentationCardData, TestCardData } from '../Common/learnableCards/utils';

export type SetErrorAction = {
  type: 'SET_ERROR';
  error: LearningSessionError;
};

export type SelectAnswerAction = {
  type: 'TENTATIVELY_SELECT_ANSWER';
  answer: string;
};

export type AnswerTestAction = {
  type: 'ANSWER_TEST';
  answer: string;
};

export type SkipTestAction = {
  type: 'SKIP_TEST';
};

export type ShowAnswerFeedbackAction = {
  type: 'SHOW_FEEDBACK';
  correctness: Correctness;
};

export type InitializeAction = {
  type: 'INITIALIZE_PROGRESS_PREFERENCES';
  learnablesWithProgress: LearnableWithProgress[];
};

export type MarkLearnableDifficultAction = {
  type: 'UPDATE_LEARNABLE_DIFFICULT';
  learnableId: LearnableId;
};

export type ToggleDifficultAction = {
  type: 'TOGGLE_DIFFICULT';
  learnableId: LearnableId;
};

export type ToggleIgnoredAction = {
  type: 'TOGGLE_IGNORED';
  learnableId: LearnableId;
};

export type ResetAnswerAction = {
  type: 'RESET_ANSWER';
};

export type PresentSummaryAction = {
  type: 'PRESENT_SUMMARY';
  summary: {
    endOfSessionPoints: Summary['endOfSessionPoints'];
  };
};

export type PresentPresentationCardAction = {
  type: 'PRESENT_PRESENTATION_CARD';
  payload: {
    sessionPercentComplete: BaseState['session']['percentComplete'];
    sessionItemCount: BaseState['session']['itemCount'];
    growthLevel: CardState['growthLevel'];
    card: PresentationCard;
    data: PresentationCardData;
    learnableWithProgress: LearnableWithProgress;
  };
};

export type PresentTestCardAction = {
  type: 'PRESENT_TEST_CARD';
  payload: {
    sessionPercentComplete: BaseState['session']['percentComplete'];
    sessionItemCount: BaseState['session']['itemCount'];
    growthLevel: CardState['growthLevel'];
    card: TestCard;
    data: TestCardData;
    learnableWithProgress: LearnableWithProgress;
  };
};

export type PresentLoadingAction = {
  type: 'PRESENT_LOADING';
};

export type UpdateGrowthLevelAction = {
  type: 'UPDATE_GROWTH_LEVEL';
  growthLevel: number;
};

export type SetEndOfSessionDataAction = {
  type: 'SET_END_OF_SESSION_DATA';
  endOfSessionData: {
    totalSessionPoints: Summary['totalSessionPoints'];
    nextSession: Summary['nextSession'];
    summaryItems: Summary['summaryItems'];
    userPath: Summary['userPath'];
    userScenario: Summary['userScenario'];
    dailyNewWords?: Summary['dailyNewWords'];
  };
};

export type LearningSessionAction =
  | SetErrorAction
  | SelectAnswerAction
  | AnswerTestAction
  | SkipTestAction
  | ShowAnswerFeedbackAction
  | ResetAnswerAction
  | InitializeAction
  | MarkLearnableDifficultAction
  | ToggleDifficultAction
  | ToggleIgnoredAction
  | PresentPresentationCardAction
  | PresentTestCardAction
  | PresentSummaryAction
  | PresentLoadingAction
  | UpdateGrowthLevelAction
  | SetEndOfSessionDataAction;

/**
 * The current state for the learning session.
 * Note that this refers to the *session as a whole*, not an answer.
 */
export type ResponseState =
  | 'Unanswered' // The user has taken no action
  | 'Selected' // The user has selected, but not confirmed the answer e.g. with the AudioMCResponse
  | 'Processing' // The user has chosen to answer but correctness evaluation is pending
  | 'Correct' // The user has confirmed their answer, and it has been marked correct
  | 'Incorrect' // The user has confirmed their answer, and it has been marked correct
  | 'NearlyCorrect'; // The user has confirmed their answer, and it has been marked nearly correct. Only occurs on some test types

type Stage = 'loading' | 'card' | 'summaryLoading' | 'summary';

interface BaseState {
  /**
   * Which top-level, finite state the session can be in
   */
  stage: Stage;

  /** Top-level data for the whole session */
  session: {
    error: LearningSessionError | null;
    progressPreferences: { [key: number]: ProgressPreferences };
    percentComplete: number;
    /** Total screens (cards) queued for this session; drives the blob progress bar */
    itemCount: number;
  };

  card: CardState | null;
  summary: Summary | Pick<Summary, 'endOfSessionPoints'> | null;
}

export type LearningSessionLoadingState = BaseState & {
  stage: 'loading';
};

/** Data for the current learnable card */
type CardState = {
  growthLevel: number;
  data: CardData;
  card: Card;
  userAnswer: string;
  responseState: ResponseState;
};

export interface LearningSessionCardState extends BaseState {
  stage: 'card';
  card: CardState;
  learnableWithProgress: LearnableWithProgress;
}

/**
 * Data for the summary screens such as EndOfSession, EndOfSpeed and
 * DailyGoalScreen
 */
export type Summary = {
  /**
   * Bonus points added at the end of the session. NOT all of the points
   * in the session.
   */
  endOfSessionPoints: number;
  totalSessionPoints: number;
  /** Single Continue Button and Mode Selector information */
  nextSession: NextSession;
  /**
   * Learned/reviewed items to show on EOS screen
   *
   * We would like to replace the duplicate state on summaryItems with
   * derived state from learningEvents or similar
   *
   * Please delete this property at your earliest convenience.
   */
  summaryItems: SummaryItem[];
  userPath: UserPath | null;
  userScenario: UserScenario | null;
  /** Only sent for mixed sessions */
  dailyNewWords?: DailyNewWords;
};

export interface LearningSessionSummaryLoadingState extends BaseState {
  stage: 'summaryLoading';
  summary: Pick<Summary, 'endOfSessionPoints'>;
  card: null;
}

export interface LearningSessionSummaryState extends BaseState {
  stage: 'summary';
  summary: Summary;
  card: null;
}

export type LearningSessionState =
  | LearningSessionCardState
  | LearningSessionLoadingState
  | LearningSessionSummaryLoadingState
  | LearningSessionSummaryState;

export const initialSessionState: LearningSessionState = {
  stage: 'loading',
  session: {
    error: null,
    progressPreferences: {},
    percentComplete: 0,
    itemCount: 0,
  },
  card: null,
  summary: null,
};

export const learningSessionReducer = (
  state: LearningSessionState,
  action: LearningSessionAction,
): LearningSessionState => {
  switch (action.type) {
    case 'SET_ERROR': {
      return {
        ...state,
        session: {
          ...state.session,
          error: action.error,
        },
      };
    }
    case 'TENTATIVELY_SELECT_ANSWER': {
      /* istanbul ignore next: only to aid type inference */
      if (state.stage !== 'card') {
        throw new Error('Must have be in Card state to tentatively select an answer');
      }
      if (state.card.responseState === 'Unanswered' || state.card.responseState === 'Selected') {
        return {
          ...state,
          card: {
            ...state.card,
            responseState: 'Selected',
            userAnswer: action.answer,
          },
        };
      }
      // If called after ANSWER_TEST, don't update again
      return state;
    }
    case 'ANSWER_TEST': {
      // Fix for Firefox to allow immediately jump from one card to the next using the Enter key
      (document.activeElement as HTMLElement).blur();

      if (
        state.stage === 'card' &&
        (state.card.responseState === 'Unanswered' || state.card.responseState === 'Selected')
      )
        return {
          ...state,
          card: {
            ...state.card,
            responseState: 'Processing',
            userAnswer: action.answer,
          },
        };

      // If called twice to process the answer, only update for the first
      return state;
    }
    case 'SKIP_TEST': {
      /* istanbul ignore next: only to aid type inference */
      if (state.stage !== 'card') {
        throw new Error('Must be in card state to SKIP_TEST');
      }
      return {
        ...state,
        card: {
          ...state.card,
          responseState: 'Processing',
          userAnswer: '',
        },
      };
    }
    case 'SHOW_FEEDBACK': {
      /* istanbul ignore next: only to aid type inference */
      if (state.stage !== 'card') {
        throw new Error('Must be in Card state to SHOW_FEEDBACK');
      }
      return {
        ...state,
        card: {
          ...state.card,
          responseState: action.correctness,
        },
      };
    }
    case 'RESET_ANSWER': {
      /* istanbul ignore next: only to aid type inference */
      if (state.stage !== 'card') {
        throw new Error(
          `Must be in Card state to RESET_ANSWER. Stage: ${JSON.stringify(
            state.stage,
          )}, State: ${JSON.stringify(state)}`,
        );
      }
      return {
        ...state,
        card: {
          ...state.card,
          responseState: 'Unanswered',
          userAnswer: '',
        },
      };
    }
    case 'INITIALIZE_PROGRESS_PREFERENCES': {
      const { learnablesWithProgress } = action;
      return {
        ...state,
        session: {
          ...state.session,
          progressPreferences: learnablesWithProgress
            .map(({ progress, learnable }) => ({
              learnableId: learnable.id,
              difficult: progress?.isDifficult || false,
              ignored: progress?.ignored || false,
              notDifficult: progress?.notDifficult || false,
              starred: progress?.starred || false,
            }))
            .reduce(
              (accumulated, { learnableId, ...progressPreferences }) => ({
                ...accumulated,
                [learnableId]: { learnableId, ...progressPreferences },
              }),
              {},
            ),
        },
      };
    }
    case 'TOGGLE_IGNORED': {
      const { learnableId } = action;
      const { progressPreferences } = state.session;
      return {
        ...state,
        session: {
          ...state.session,
          progressPreferences: {
            ...progressPreferences,
            [learnableId]: {
              ...progressPreferences[learnableId],
              ignored: !progressPreferences[learnableId].ignored,
            },
          },
        },
      };
    }
    case 'TOGGLE_DIFFICULT': {
      const { learnableId } = action;
      const { progressPreferences } = state.session;
      return {
        ...state,
        session: {
          ...state.session,
          progressPreferences: {
            ...progressPreferences,
            [learnableId]: {
              ...progressPreferences[learnableId],
              difficult: !progressPreferences[learnableId].difficult,
              notDifficult: progressPreferences[learnableId].difficult, // difficult + toggle = notDifficult
              starred: !progressPreferences[learnableId].difficult, // !difficult + toggle = starred
            },
          },
        },
      };
    }
    case 'UPDATE_LEARNABLE_DIFFICULT': {
      const { learnableId } = action;
      const { progressPreferences } = state.session;
      return {
        ...state,
        session: {
          ...state.session,
          progressPreferences: {
            ...progressPreferences,
            [learnableId]: {
              ...progressPreferences[learnableId],
              difficult: true,
            },
          },
        },
      };
    }
    case 'PRESENT_PRESENTATION_CARD': {
      const { payload } = action;
      return {
        ...state,
        stage: 'card',
        session: {
          ...state.session,
          percentComplete: payload.sessionPercentComplete,
          itemCount: payload.sessionItemCount,
        },
        card: {
          growthLevel: payload.growthLevel,
          card: payload.card,
          data: payload.data,
          userAnswer: '',
          responseState: 'Unanswered',
        },
        learnableWithProgress: payload.learnableWithProgress,
      };
    }
    case 'PRESENT_TEST_CARD': {
      const { payload } = action;
      return {
        ...state,
        stage: 'card',
        session: {
          ...state.session,
          percentComplete: payload.sessionPercentComplete,
          itemCount: payload.sessionItemCount,
        },
        card: {
          growthLevel: payload.growthLevel,
          card: payload.card,
          data: payload.data,
          userAnswer: '',
          responseState: 'Unanswered',
        },
        learnableWithProgress: payload.learnableWithProgress,
      };
    }
    case 'PRESENT_LOADING': {
      return {
        ...state,
        stage: 'loading',
        card: null,
        summary: null,
      };
    }
    case 'PRESENT_SUMMARY': {
      return {
        ...state,
        stage: 'summaryLoading',
        card: null,
        summary: {
          endOfSessionPoints: action.summary.endOfSessionPoints,
        },
      };
    }
    case 'SET_END_OF_SESSION_DATA': {
      if (state.stage !== 'summaryLoading' && state.stage !== 'summary') {
        // must be in either summaryLoading OR summary state to SET_END_OF_SESSION_DATA
        throw new Error('Invalid stage! State: ' + JSON.stringify(state));
      }
      return {
        ...state,
        stage: 'summary',
        summary: {
          ...state.summary,
          ...action.endOfSessionData,
        },
      };
    }
    case 'UPDATE_GROWTH_LEVEL': {
      if (state.stage === 'card') {
        return {
          ...state,
          card: {
            ...state.card,
            growthLevel: action.growthLevel,
          },
        };
      }

      /* istanbul ignore next: shouldn't occur */
      return state;
    }
  }
};
